import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { formatAddress, formatETH } from '../lib/utils';
import { getBetById, acceptBet as acceptBetApi, resolveBet, cancelBet as cancelBetApi } from '../lib/api';
import { useAcceptBet, useCancelBet } from '../hooks/useContract';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { OracleResolutionPanel } from '../components/OracleResolutionPanel';
import { OracleDisputePanel } from '../components/OracleDisputePanel';
import { OracleSettlementPanel } from '../components/OracleSettlementPanel';
import { SocialSharePanel } from '../components/SocialShare';
import { BetDetailHeader } from '../components/bets/BetDetailHeader';
import {
    BetStakesCard,
    BetParticipantsCard,
    BetWinnerCard,
    BetTransactionHistory,
} from '../components/bets/BetDetailCards';
import { BetActionsPanel } from '../components/bets/BetActionsPanel';
import { toast } from 'sonner';
import { UMA_ORACLE_ADDRESSES } from '../lib/contract';
import { ArrowLeft, Timer, ExternalLink } from 'lucide-react';
import { isPast } from 'date-fns';
import { fr, es, de, it, pt, ja, ko, zhCN, ar, hi, ru, tr, pl, nl, uk, vi, th, sv, ro, cs, sk, hu, nb, da, fi, id, he, enUS } from 'date-fns/locale';

const DATE_LOCALES = {
    en: enUS, fr, es, de, it, pt, ja, ko, zh: zhCN, ar, hi, ru, tr, pl, nl, uk, vi, th, sv, ro, cs, sk, hu, no: nb, da, fi, id, he,
};

export function BetDetailPage() {
    const { t, i18n } = useTranslation();
    const dateLocale = DATE_LOCALES[i18n.language] || DATE_LOCALES[i18n.language?.split('-')[0]] || enUS;
    const { id } = useParams();
    const navigate = useNavigate();
    const { address, isConnected, connect, balance, chainId } = useWeb3();

    const {
        acceptBet: acceptBetOnChain, hash: acceptHash,
        isPending: isAcceptPending, isConfirming: isAcceptConfirming,
        isSuccess: isAcceptSuccess, error: acceptError,
    } = useAcceptBet();

    const {
        cancelBet: cancelBetOnChain, hash: cancelHash,
        isPending: isCancelPending, isConfirming: isCancelConfirming,
        isSuccess: isCancelSuccess, error: cancelError,
    } = useCancelBet();

    const [bet, setBet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const isCreator = address?.toLowerCase() === bet?.creator_address?.toLowerCase();
    const isTaker = address?.toLowerCase() === bet?.taker_address?.toLowerCase();
    const canTake = bet?.status === 'open' && !isCreator && isConnected;
    const canCancel = bet?.status === 'open' && isCreator;
    const canResolve = bet?.status === 'active' && isPast(new Date(bet?.resolution_time || 0));
    const hasEnoughBalance = balance >= (bet?.taker_stake || 0);
    const isCorrectChain = chainId === 8453;

    useEffect(() => {
        if (isAcceptSuccess && acceptHash) {
            toast.success('Bet accepted on blockchain!', {
                description: (
                    <a href={`https://basescan.org/tx/${acceptHash}`} target="_blank" rel="noopener noreferrer"
                        className="text-[#00FF66] hover:underline flex items-center gap-1">
                        View transaction <ExternalLink className="w-3 h-3" />
                    </a>
                ),
            });
            acceptBetApi(id, address).then(updated => setBet(updated)).catch(console.error);
        }
    }, [isAcceptSuccess, acceptHash, id, address]);

    useEffect(() => {
        if (isCancelSuccess && cancelHash) {
            toast.success('Bet cancelled on blockchain!', { description: 'Your stake has been returned.' });
            navigate('/bets');
        }
    }, [isCancelSuccess, cancelHash, navigate]);

    useEffect(() => {
        if (acceptError) {
            toast.error('Failed to accept bet', { description: acceptError.shortMessage || acceptError.message });
        }
        if (cancelError) {
            toast.error('Failed to cancel bet', { description: cancelError.shortMessage || cancelError.message });
        }
    }, [acceptError, cancelError]);

    useEffect(() => {
        async function fetchBet() {
            try {
                const data = await getBetById(id);
                setBet(data);
            } catch (error) {
                console.error('Failed to fetch bet:', error);
                toast.error('Bet not found');
            } finally {
                setLoading(false);
            }
        }
        fetchBet();
    }, [id]);

    const handleTakeBet = async () => {
        if (!isConnected) { await connect(); return; }
        if (!isCorrectChain) {
            toast.error('Wrong network', { description: 'Please switch to Base network' });
            return;
        }
        if (!hasEnoughBalance) {
            toast.error('Insufficient balance', { description: `You need ${formatETH(bet.taker_stake)} ETH to take this bet.` });
            return;
        }
        if (bet.on_chain_id) {
            try { await acceptBetOnChain(bet.on_chain_id, bet.taker_stake); }
            catch (error) { console.error('Accept bet error:', error); }
        } else {
            setActionLoading('take');
            try {
                const updated = await acceptBetApi(id, address);
                setBet(updated);
                toast.success('Bet accepted!', { description: 'You have successfully taken this bet. Good luck!' });
            } catch (error) {
                toast.error('Failed to take bet', { description: error.response?.data?.detail || 'Please try again' });
            } finally { setActionLoading(null); }
        }
    };

    const handleCancel = async () => {
        if (!isCorrectChain) {
            toast.error('Wrong network', { description: 'Please switch to Base network' });
            return;
        }
        if (bet.on_chain_id) {
            try { await cancelBetOnChain(bet.on_chain_id); }
            catch (error) { console.error('Cancel bet error:', error); }
        } else {
            setActionLoading('cancel');
            try {
                await cancelBetApi(id, address);
                toast.success('Bet cancelled', { description: 'Your stake has been returned.' });
                navigate('/bets');
            } catch (error) {
                toast.error('Failed to cancel bet', { description: error.response?.data?.detail || 'Please try again' });
            } finally { setActionLoading(null); }
        }
    };

    const handleResolve = async (outcome) => {
        setActionLoading(outcome);
        try {
            const updated = await resolveBet(id, outcome);
            setBet(updated);
            toast.success('Bet resolved!', { description: `Winner: ${formatAddress(updated.winner_address)}` });
        } catch (error) {
            toast.error('Failed to resolve bet', { description: error.response?.data?.detail || 'Please try again' });
        } finally { setActionLoading(null); }
    };

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Skeleton className="h-8 w-32 mb-8 bg-white/10" />
                <Skeleton className="h-12 w-3/4 mb-4 bg-white/10" />
                <Skeleton className="h-64 w-full mb-8 bg-white/10" />
            </div>
        );
    }

    if (!bet) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
                <div className="w-20 h-20 rounded-full bg-[#00FF66]/10 flex items-center justify-center mx-auto mb-6">
                    <Timer className="w-10 h-10 text-[#00FF66]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">{t('betDetail.betNotFound')}</h2>
                <p className="text-white/50 mb-8">{t('betDetail.betNotFoundDesc')}</p>
                <Link to="/bets">
                    <Button variant="outline" className="border-white/10 text-white">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {t('betDetail.backToBets')}
                    </Button>
                </Link>
            </div>
        );
    }

    const totalPool = (bet.creator_stake || 0) + (bet.taker_stake || 0);
    const isUserWinner = bet.status === 'resolved' && bet.winner_address?.toLowerCase() === address?.toLowerCase();

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="bet-detail-page">
            <Link to="/bets" className="inline-flex items-center text-white/50 hover:text-[#00FF66] transition-colors mb-8">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('betDetail.backToBets')}
            </Link>

            <BetDetailHeader bet={bet} isCreator={isCreator} isTaker={isTaker} dateLocale={dateLocale} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <BetStakesCard bet={bet} totalPool={totalPool} />
                    <BetParticipantsCard bet={bet} isCreator={isCreator} isTaker={isTaker} />
                    <BetWinnerCard bet={bet} />
                    <BetTransactionHistory bet={bet} />

                    <OracleResolutionPanel bet={bet} isCreator={isCreator} isTaker={isTaker} chainId={chainId} address={address} />
                    <OracleDisputePanel bet={bet} chainId={chainId} address={address} oracleAddress={UMA_ORACLE_ADDRESSES[chainId]} />
                    <OracleSettlementPanel bet={bet} chainId={chainId} address={address} oracleAddress={UMA_ORACLE_ADDRESSES[chainId]} />

                    {isUserWinner && (
                        <SocialSharePanel
                            bet={bet}
                            winAmount={bet.payout_amount || (bet.creator_stake + bet.taker_stake) * 0.97}
                            userRole={isCreator ? 'creator' : 'taker'}
                            isWinner={true}
                        />
                    )}
                </div>

                <div className="space-y-6">
                    <BetActionsPanel
                        bet={bet}
                        canTake={canTake}
                        canCancel={canCancel}
                        canResolve={canResolve}
                        isCorrectChain={isCorrectChain}
                        hasEnoughBalance={hasEnoughBalance}
                        totalPool={totalPool}
                        actionLoading={actionLoading}
                        acceptHash={acceptHash}
                        cancelHash={cancelHash}
                        isAcceptPending={isAcceptPending}
                        isAcceptConfirming={isAcceptConfirming}
                        isCancelPending={isCancelPending}
                        isCancelConfirming={isCancelConfirming}
                        onTake={handleTakeBet}
                        onCancel={handleCancel}
                        onResolve={handleResolve}
                        dateLocale={dateLocale}
                    />
                </div>
            </div>
        </div>
    );
}
